import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';

import { Observable, catchError, map, of } from 'rxjs';

import { MonitoringLogger } from '@fcom/core';
import { stopPropagation } from '@fcom/core/utils';
import { Cabin, FinnairCabinClass } from '@fcom/dapi/api/models';
import { CmsCollection, CmsTemplate } from '@fcom/core-api';
import { ChipTheme, TabLayoutType } from '@fcom/ui-components';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces';
import { GtmService } from '@fcom/common/gtm';
import { FinnairThemeService, FragmentService } from '@fcom/common/services';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

interface CabinClassContent {
  id: string;
  template: Observable<CmsTemplate>;
}

@Component({
  selector: 'fin-cabin-class-modal',
  styleUrls: ['./cabin-class-modal.component.scss'],
  templateUrl: './cabin-class-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CabinClassModalComponent {
  private gtmService = inject(GtmService);
  private fragmentService = inject(FragmentService);
  private monitoringLogger = inject(MonitoringLogger);
  private themeService = inject(FinnairThemeService);

  cabinClasses = input<FinnairCabinClass[] | Cabin[]>([]);
  showTriggerChips = input(true);

  public readonly ChipTheme = ChipTheme;
  public readonly TabLayoutType = TabLayoutType;

  cabinClassModalOpen = signal(false);
  selectedCabinClassId = signal<string | undefined>(undefined);

  availableCabinClasses = computed<CabinClassContent[]>(() => {
    const cmsMap = {
      [FinnairCabinClass.ECONOMY]: { id: 'economy', url: 'fragments.cabinModalEconomy.url' },
      [FinnairCabinClass.ECOPREMIUM]: { id: 'ecopremium', url: 'fragments.cabinModalEcopremium.url' },
      [FinnairCabinClass.BUSINESS]: { id: 'business', url: 'fragments.cabinModalBusiness.url' },
    };

    return this.cabinClasses()
      .filter((cabinClass): cabinClass is keyof typeof cmsMap => cabinClass in cmsMap)
      .map((cabinClass) => ({
        id: cmsMap[cabinClass].id,
        template: this.fragmentService.getFragment<CmsCollection>(cmsMap[cabinClass].url).pipe(
          map((fragment) => ({ header: [], main: [fragment], footer: [] })),
          catchError((error: unknown) => {
            this.monitoringLogger.error('Error loading cabin class modal fragment', { error });
            return of({ header: [], main: [], footer: [] });
          })
        ),
      }));
  });

  isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  openModal(e: Event, id: string): void {
    stopPropagation(e);
    this.selectedCabinClassId.set(id);
    this.cabinClassModalOpen.set(true);
    this.gtmService.trackElement(
      'open-cabin-class-chip',
      GaContext.FLIGHT_SELECTION,
      ElementTypes.BUTTON,
      id,
      ElementActions.CLICK
    );
  }
}
